import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { Switch } from '@mui/material';
import { set_ui_settings } from 'console/redux/settings-actions';
import { UniversalMenu } from 'console/shared/components/menu';
import { DeviceConfig } from 'console/shared/contexts/device/config/types';
import { CustomIcon } from 'console/shared/components/icon/custom';
import GuardIcon from './icons/zta-guard.guard=true.svg';
import SirenIcon from './icons/zta-siren.siren=true.svg';
import EngineIcon from './icons/control-button-engine-block.grey.svg';
import AutoStartIcon from './icons/control-button-autostart.grey.svg';
import { SportsHandball, Warning } from '@mui/icons-material';
import { useAction } from 'shared/hooks/redux/hooks';
export const AudioSettingsMenu = memo(function AudioSettingsMenu(props) {
    const { anchor, onClose, app_sounds, device_audio, silent_mode, has_silent_mode, has_handsfree, onChangeSilentMode, } = props;
    const { auto_start_audio, guard_audio, engine_audio, siren_audio } = device_audio;
    const { guard, siren, engine, auto_start } = useMemo(() => {
        if (app_sounds) {
            return app_sounds;
        }
        return { guard: false, siren: false, engine: false, auto_start: false };
    }, [app_sounds]);
    const { device_id } = useContext(DeviceConfig);
    const setUISettings = useAction(set_ui_settings);
    const onChangeDeviceStateSounds = useCallback((what, value) => {
        setUISettings({
            device_state_sounds: {
                [device_id]: { app_sounds: { [what]: value } },
            },
        });
    }, [device_id]);
    const onGuardChange = useCallback(() => {
        onChangeDeviceStateSounds('guard', !guard);
    }, [guard]);
    const onSirenChange = useCallback(() => onChangeDeviceStateSounds('siren', !siren), [siren]);
    const onEngineChange = useCallback(() => onChangeDeviceStateSounds('engine', !engine), [engine]);
    const onAutoStartChange = useCallback(() => onChangeDeviceStateSounds('auto_start', !auto_start), [auto_start]);
    const onSirenAlarmChange = useCallback(() => {
        onChangeSilentMode('siren', 'alarm', !silent_mode?.siren.alarm);
    }, [onChangeSilentMode, silent_mode]);
    const onSirenGuardChange = useCallback(() => {
        onChangeSilentMode('siren', 'guard', !silent_mode?.siren.guard);
    }, [onChangeSilentMode, silent_mode]);
    const onSirenHandsFreeChange = useCallback(() => {
        onChangeSilentMode('siren', 'handsfree', !silent_mode?.siren.handsfree);
    }, [onChangeSilentMode, silent_mode]);
    const app_sounds_group = useMemo(() => [
        {
            icon: _jsx(CustomIcon, { fontSize: "0.9em", color: "action", Icon: GuardIcon }),
            label: 'Охрана',
            onClick: onGuardChange,
            additional_view: (_jsx(Switch, { size: "small", checked: guard, color: "primary", onClick: onGuardChange })),
            show: guard_audio,
        },
        {
            icon: (_jsx(CustomIcon, { viewBox: "0 0 38 38", Icon: SirenIcon, color: "action" })),
            label: 'Сирена',
            onClick: onSirenChange,
            additional_view: (_jsx(Switch, { size: "small", checked: siren, color: "primary", onClick: onSirenChange })),
            show: siren_audio,
        },
        {
            icon: (_jsx(CustomIcon, { viewBox: "0 0 40 40", Icon: EngineIcon, color: "action" })),
            label: 'Двигатель',
            onClick: onEngineChange,
            additional_view: (_jsx(Switch, { size: "small", checked: engine, color: "primary", onClick: onEngineChange })),
            show: engine_audio,
        },
        {
            icon: (_jsx(CustomIcon, { fontSize: "0.9em", Icon: AutoStartIcon, color: "action" })),
            label: 'Автозапуск',
            onClick: onAutoStartChange,
            additional_view: (_jsx(Switch, { size: "small", checked: auto_start, color: "primary", onClick: onAutoStartChange })),
            show: auto_start_audio,
        },
    ], [
        app_sounds,
        device_audio,
        onAutoStartChange,
        onEngineChange,
        onGuardChange,
        onSirenChange,
    ]);
    const siren_sounds_group = useMemo(() => [
        {
            icon: _jsx(CustomIcon, { fontSize: "0.9em", Icon: Warning, color: "action" }),
            label: 'Тревога',
            onClick: onSirenAlarmChange,
            additional_view: (_jsx(Switch, { size: "small", checked: !silent_mode?.siren.alarm, color: "primary", onClick: onSirenAlarmChange })),
        },
        {
            icon: _jsx(CustomIcon, { fontSize: "0.9em", Icon: GuardIcon, color: "action" }),
            label: 'Охрана',
            onClick: onSirenGuardChange,
            additional_view: (_jsx(Switch, { size: "small", checked: !silent_mode?.siren.guard, color: "primary", onClick: onSirenGuardChange })),
        },
        {
            icon: (_jsx(CustomIcon, { fontSize: "0.9em", Icon: SportsHandball, color: "action" })),
            label: 'Свободные руки',
            onClick: onSirenHandsFreeChange,
            additional_view: (_jsx(Switch, { size: "small", checked: !silent_mode?.siren.handsfree, color: "primary", onClick: onSirenHandsFreeChange })),
            show: has_handsfree,
        },
    ], [
        onSirenGuardChange,
        silent_mode,
        has_handsfree,
        onSirenAlarmChange,
        onSirenHandsFreeChange,
    ]);
    const menu_items = useMemo(() => [
        {
            group_label: 'Озвучивание приложением',
            group_items: app_sounds_group,
        },
        {
            group_label: 'Озвучивание сиреной',
            group_items: siren_sounds_group,
            show: has_silent_mode,
        },
    ], [app_sounds_group, siren_sounds_group, has_silent_mode]);
    return (_jsx(UniversalMenu, { menu_items: menu_items, anchor: anchor, opened: !!anchor, onClose: onClose }));
});
